import React, {
    useReducer,
    useCallback,
    useEffect,
    useContext,
    useMemo,
    memo,
} from 'react';
import { withCookies } from 'react-cookie';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import DetailsHeader from '../components/batches/Details/DetailsHeader';
import DetailsVehicleCard from '../components/batches/Details/DetailsVehicleCard';
import ManagementList from '../components/batches/Management/ManagementList';
import DetailsSummary from '../components/batches/Details/DetailsSummary';
import DetailsModal from '../components/batches/Details/DetailsModal';
import Traductor from '../components/Traductor';
import EditNameForm from '../components/batches/Details/EditNameForm';
import { ReactComponent as ProposalImage } from '../images/icons/proposal-sent-icon.svg';
import ScrollToTop from '../components/ScrollToTop';
import DetailsModalOptions from '../components/batches/Details/DetailsPriceForm';
import ModalDetailVehicle from 'components/auctions/ModalDetailVehicle';

import * as actions from '../redux/actions/batches';
import batchService from '../services/BatchesService';
import Globals from '../Globals';
import { VehiclesContext } from '../contexts/VehiclesContext';
import { getWSService } from '../js/WebSocketService';
import { checkAuctionIsActive } from 'js/components/shared/vehicleFunctions';
import { isAppleDeviceOrNavegatorForDates } from 'Functions';
import useServerTime from 'hooks/useServerTime';

const initialBatchDetailsState = {
    batch: null,
    deleteModalVisible: false,
    successNewProposalModalVisible: false,
    deleteVehicleErrorModalVisible: false,
    editNameModalVisible: false,
    errorModal: '',
    selectedVehicle: null,
    amountsForm: {},
};

const BatchDetailsReducer = (state, { type, payload }) => {
    switch (type) {
        case 'SET_BATCH':
            return {
                ...state,
                batch: payload,
                amountsForm: payload.cars.reduce(
                    (acc, curr) => ({
                        ...acc,
                        [curr.IdPublish]: curr.AmountOffered
                            ? curr.AmountOffered
                            : '',
                    }),
                    {}
                ),
            };
        case 'UPDATE_NAME':
            return {
                ...state,
                batch: {
                    ...state.batch,
                    details: { ...state.batch.details, Name: payload },
                },
            };
        case 'SELECT_VEHICLE':
            return { ...state, selectedVehicle: payload };
        case 'UPDATE_SELECTED_VEHICLE':
            return { ...state, selectedVehicle: payload };
        case 'UNSELECT_VEHICLE':
            return { ...state, selectedVehicle: null };
        case 'UPDATE_VEHICLE_AMOUNT':
            return {
                ...state,
                amountsForm: {
                    ...state.amountsForm,
                    [payload.IdPublish]: payload.amount.replaceAll('.', ''),
                },
            };
        case 'DELETE_VEHICLE_FROM_BATCH':
            const { IdPublish, AmountOffered } = payload;
            const {
                [IdPublish]: deletedId,
                ...filteredAmounts
            } = state.amountsForm;
            return {
                ...state,
                batch: {
                    ...state.batch,
                    details: {
                        ...state.batch.details,
                        Amount: state.batch.details.Amount - AmountOffered,
                        NumVehicles: state.batch.details.NumVehicles - 1,
                    },
                    cars: state.batch.cars.filter(
                        car => car.IdPublish !== IdPublish
                    ),
                },
                amountsForm: { ...filteredAmounts },
            };
        case 'UPDATE_VEHICLES':
            const updatedBatch = {
                ...state.batch,
                details: {
                    ...state.batch.details,
                    IdStatus: 1,
                    Amount: payload.Amount,
                    NumVehicles: payload.NumVehicles,
                },
                cars: state.batch.cars.map(car => ({
                    ...car,
                    AmountOffered: +state.amountsForm[car.IdPublish],
                    LastAmount: payload.Vehicles.find(
                        vehicle => vehicle.IdWebVehicles === car.IdPublish
                    ).LastAmount,
                })),
            };
            localStorage.setItem(
                'selectedBatchDetails',
                JSON.stringify(updatedBatch.details)
            );
            return {
                ...state,
                batch: updatedBatch,
                amountsForm: payload.Vehicles.reduce(
                    (acc, curr) => ({
                        ...acc,
                        [curr.IdWebVehicles]: curr.Amount,
                    }),
                    {
                        ...state.amountsForm,
                    }
                ),
                successNewProposalModalVisible: true,
            };
        case 'SHOW_DELETE':
            return {
                ...state,
                deleteModalVisible: true,
            };
        case 'SET_ERROR':
            return { ...state, errorModal: state.errorModal ? '' : payload };
        case 'SHOW_DELETE_ERROR':
            return {
                ...state,
                deleteVehicleErrorModalVisible: true,
            };
        case 'SHOW_EDIT':
            return {
                ...state,
                editNameModalVisible: true,
            };
        case 'HIDE_MODALS':
            return {
                ...state,
                deleteModalVisible: false,
                successNewProposalModalVisible: false,
                deleteVehicleErrorModalVisible: false,
                editNameModalVisible: false,
            };
        default:
            return state;
    }
};

const BatchDetailsPage = ({ cookies, history, match, location, cars }) => {
    const [
        {
            deleteModalVisible,
            successNewProposalModalVisible,
            batch,
            deleteVehicleErrorModalVisible,
            editNameModalVisible,
            selectedVehicle,
            amountsForm,
            errorModal,
        },
        dispatch,
    ] = useReducer(BatchDetailsReducer, initialBatchDetailsState);

    const { setSelectedVehicle } = useContext(VehiclesContext);

    const t = useMemo(() => new Traductor(), []);
    const { serverTime } = useServerTime(true);
    const disabledAdd = useMemo(
        () =>
            batch?.details?.IdStatus === '4' ||
            batch?.details?.IdStatus === '2' ||
            batch?.details?.IdStatus === '7' ||
            !batch?.details?.Amount ||
            !checkAuctionIsActive(
                isAppleDeviceOrNavegatorForDates(batch?.details?.CreateDate),
                isAppleDeviceOrNavegatorForDates(batch?.details?.EndDate),
                serverTime
            ),
        [
            batch?.details?.Amount,
            batch?.details?.EndDate,
            batch?.details?.IdStatus,
            batch?.details?.CreateDate,
            serverTime,
        ]
    );

    const fetchBatchDetails = useCallback(async () => {
        const token = cookies.get(Globals.COOKIES.JWT)?.AccessToken;
        const {
            body: { Result },
        } = await batchService.getBatchDetails(
            { IdBach: match.params.value },
            token
        );

        const batchData = {
            details: location.state
                ? location.state.batch
                : JSON.parse(localStorage.getItem('selectedBatchDetails')), //if user refreshes the page, get the last data from local storage instead
            cars: Result,
        };

        dispatch({ type: 'SET_BATCH', payload: batchData });
    }, [cookies, match.params.value, location.state]);

    useEffect(() => {
        fetchBatchDetails();
    }, [fetchBatchDetails]);

    const closeModals = useCallback(() => {
        dispatch({ type: 'HIDE_MODALS' });
    }, []);

    const editBatchName = useCallback(
        async newName => {
            const token = cookies.get(Globals.COOKIES.JWT)?.AccessToken;
            const response = await batchService.editBatchName(
                { Id: match.params.value, Name: newName },
                token
            );
            if (response) {
                dispatch({ type: 'UPDATE_NAME', payload: newName });
            }
            closeModals();
        },
        [match.params.value, closeModals, cookies]
    );

    const emitDelete = useCallback(deletedIds => {
        const message = {
            Vehicles: deletedIds,
        };
        const data = {
            type: 'deleteBatch',
            data: message,
        };

        getWSService().sendMessage('onMessage', data);
    }, []);

    const onDeleteVehicleFromBatch = useCallback(
        async car => {
            if (batch?.cars.length <= 3) {
                return dispatch({ type: 'SHOW_DELETE_ERROR' });
            }
            const token = cookies.get(Globals.COOKIES.JWT)?.AccessToken;
            const {
                body: {
                    Result: { Result },
                },
            } = await batchService.deleteBatchVehicle(
                { IdBach: match.params.value, IdPublishVehicle: car.IdPublish },
                token
            );

            if (Result) {
                dispatch({ type: 'DELETE_VEHICLE_FROM_BATCH', payload: car });
                emitDelete([car.IdPublish]);
            }
        },
        [batch, cookies, emitDelete, match.params.value]
    );

    const deleteBatch = useCallback(async () => {
        const token = cookies.get(Globals.COOKIES.JWT)?.AccessToken;
        const {
            body: { Result },
        } = await batchService.deleteBatch(match.params.value, token);
        if (Result) {
            emitDelete(batch.cars.map(car => car.IdPublish));
            closeModals();
            history.push({
                pathname: Globals.APPLICATION_PATH.BATCHES,
            });
        }
    }, [cookies, match.params.value, emitDelete, batch, closeModals, history]);

    const onAddMoreVehiclesToBatch = useCallback(() => {
        //validate that any car in trolley has same date as selected batch

        if (cars.length) {
            const checkTrolleyCarsDates = cars.every(
                car =>
                    (car?.VehicleWebSales?.PublicationEndDate ||
                        car?.PublicationEndDate) === batch?.details?.EndDate
            );
            if (!checkTrolleyCarsDates) {
                return dispatch({
                    type: 'SET_ERROR',
                    payload: t.getTraduction('BatchDatesError'),
                });
            }
        }

        return history.push({
            pathname: `${Globals.APPLICATION_PATH.AUCTION}`,
            state: {
                selectedBatchToAdd: batch,
            },
        });
    }, [cars, history, batch, t]);

    const onVehicleCardPress = useCallback(
        vehicle => {
            setSelectedVehicle(vehicle);
            dispatch({ type: 'SELECT_VEHICLE', payload: vehicle });
        },
        [setSelectedVehicle]
    );

    const onChangeAmount = useCallback(
        payload => dispatch({ type: 'UPDATE_VEHICLE_AMOUNT', payload }),
        []
    );

    const onSendProposal = useCallback(
        async spinnerCallback => {
            spinnerCallback(true);
            const token = cookies.get(Globals.COOKIES.JWT)?.AccessToken;
            const amountsFormEntries = Object.entries(amountsForm);
            const changedAmountVehicles = amountsFormEntries.reduce(
                (acc, [IdPublish, Amount]) => {
                    const car = batch?.cars.find(
                        e => e.IdPublish === +IdPublish
                    );

                    if (!car || car.AmountOffered !== Amount) {
                        return [
                            ...acc,
                            { IdWebVehicles: IdPublish, Amount: +Amount },
                        ];
                    }

                    return acc;
                },
                []
            );

            const body = {
                Vehicles: changedAmountVehicles,
                IdBach: match.params.value,
            };
            const {
                body: { Result },
            } = await batchService.editBatchVehicles(body, token);

            if (Result) {
                dispatch({
                    type: 'UPDATE_VEHICLES',
                    payload: Result,
                });
            }
            spinnerCallback(false);
        },
        [cookies, amountsForm, match.params.value, batch]
    );

    const renderOptions = useCallback(
        props => (
            <DetailsModalOptions
                inputAmount={amountsForm[selectedVehicle?.IdPublish]}
                onChangeAmount={onChangeAmount}
                containerClasses={'u-mt u-pl-none'}
                {...props}
            />
        ),
        [onChangeAmount, selectedVehicle, amountsForm]
    );

    const renderVehicleCard = useCallback(
        props => (
            <DetailsVehicleCard
                onVehicleCardPress={onVehicleCardPress}
                onChangeAmount={onChangeAmount}
                inputAmount={amountsForm[props?.IdPublish]}
                batchStatus={batch?.details?.IdStatus}
                onDelete={
                    batch?.details?.IdStatus !== '4' &&
                    batch?.details?.IdStatus !== '2' &&
                    batch?.details?.IdStatus !== '7' &&
                    onDeleteVehicleFromBatch
                }
                {...props}
            />
        ),
        [
            //Do not change on form change in order to keep input focus
            batch,
            onVehicleCardPress,
            onChangeAmount,
            onDeleteVehicleFromBatch,
            selectedVehicle,
        ]
    );

    return (
        <section
            id="c-batch-details"
            className="o-content-section u-pt-small u-rema-full-height"
            data-testid="batchesDetailsPageTestId"
        >
            <ScrollToTop />
            <div className="o-page-wrap o-page-wrap--xsmall o-layout">
                <DetailsHeader
                    name={batch?.details.Name}
                    onAddVehiclesToBatch={onAddMoreVehiclesToBatch}
                    onDeleteBatch={() => dispatch({ type: 'SHOW_DELETE' })}
                    onShowEdit={() => dispatch({ type: 'SHOW_EDIT' })}
                    disabledAdd={disabledAdd}
                    batchStatus={batch?.details?.IdStatus}
                />
                <ManagementList
                    list={
                        batch?.cars || [
                            { loading: true },
                            { loading: true },
                            { loading: true },
                            { loading: true },
                        ]
                    }
                    component={renderVehicleCard}
                    valueChanging={0}
                    itemKey={'IdVehicle'}
                    amountToRender={1}
                    extraClasses={{
                        listItemClasses: 'u-pt',
                        listClasses:
                            'o-layout-item u-3/4 u-1/1@s u-inline-item  u-pr u-pr-none@s',
                    }}
                />
                <DetailsSummary
                    loading={!batch}
                    data={batch}
                    amountsForm={amountsForm}
                    onSendProposal={onSendProposal}
                />
            </div>
            <DetailsModal
                visible={successNewProposalModalVisible}
                large
                onActionPress={() =>
                    history.push(Globals.APPLICATION_PATH.BATCHES)
                }
                onClose={closeModals}
                title={t.getTraduction('SuccessProposalModalTitle')}
                content={t.getTraduction('BatchTransportReminder')}
                leftButton={t.getTraduction('Close')}
                rightButton={t.getTraduction('GoToBatchManagement')}
                img={<ProposalImage className="u-mb-large" />}
            />
            <DetailsModal
                visible={deleteModalVisible}
                onActionPress={deleteBatch}
                onClose={closeModals}
                title={t.getTraduction('DeleteBatchWarning')(
                    batch?.details.Name
                )}
                content={t.getTraduction('IrreversibleAction')}
                leftButton={t.getTraduction('Return')}
                rightButton={t.getTraduction('Delete')}
            />
            <DetailsModal
                visible={deleteVehicleErrorModalVisible}
                onActionPress={onAddMoreVehiclesToBatch}
                onClose={closeModals}
                title={t.getTraduction('DeleteErrorModalTitle')}
                content={t.getTraduction('DeleteErrorModalContent')}
                leftButton={t.getTraduction('Return')}
                rightButton={t.getTraduction('AddVehicles')}
            />
            <DetailsModal visible={editNameModalVisible}>
                <EditNameForm onCancel={closeModals} onSubmit={editBatchName} />
            </DetailsModal>
            <DetailsModal
                visible={errorModal}
                onActionPress={() => dispatch({ type: 'SET_ERROR' })}
                onClose={() => dispatch({ type: 'SET_ERROR' })}
                title={t.getTraduction('BatchErrorTitle')}
                content={errorModal}
                leftButton={t.getTraduction('Close')}
                rightButton={t.getTraduction('Accept')}
            />
            {selectedVehicle && (
                <ModalDetailVehicle
                    vehicle={selectedVehicle}
                    onShowDetail={() => dispatch({ type: 'UNSELECT_VEHICLE' })}
                    printCountDown={() => null}
                    printFavouriteIcon={() => null}
                    printWebSalesOptions={props => renderOptions(props)}
                    updateVehiclesList={payload =>
                        dispatch({ type: 'UPDATE_SELECTED_VEHICLE', payload })
                    }
                    tagMaxBidStatus={() => null}
                    fetchServerTime={() => null}
                    inactive={false}
                />
            )}
        </section>
    );
};

const mapStateToProps = (state, props) => ({
    cars: state.carsBatch.cars,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(actions, dispatch),
});

export default memo(
    withCookies(
        withRouter(
            connect(mapStateToProps, mapDispatchToProps)(BatchDetailsPage)
        )
    )
);
